import { IBaseProjectPosition, ICreateProjectPosition, IProjectPosition } from '../../../interfaces/projectPosition';
import { IProjectApplicant } from '../../../interfaces/projectApplicant';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../common/boxes';
import PositionsTable from '../../common/project-positions/PositionsTable';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import PositionEntityModal from '../../common/project-positions/position-entity-modal/PositionEntityModal';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { TEXTS } from '../../../core/texts';
import { Add } from '@mui/icons-material';
import { IProjectApplicantRequest } from '../../../interfaces/projectApplicantRequest';
import PositionApplicantsModal from '../../common/project-positions/position-applicants-modal/PositionApplicantsModal';
import AddApplicantModal from '../../common/project-positions/position-applicants-modal/AddApplicantModal';
import { IProjectOpportunity } from '../../../interfaces/projectOpportunity';

export interface IProjectPositionsProps {
  project: IProjectOpportunity,
  positions: IProjectPosition[],
  refreshPositions: () => void,
  handleAddNewPosition: (position: ICreateProjectPosition) => Promise<IProjectPosition>;
  handleRemovePosition: (positionId: string) => Promise<IProjectPosition>;
  handleUpdatePosition: (positionId: string, position: IBaseProjectPosition) => Promise<IProjectPosition>;
  handleGetApplicants: (positionId: string) => Promise<IProjectApplicant[]>;
  handleAddApplicant?: (applicant: IProjectApplicantRequest) => Promise<IProjectApplicant>;
  handleAddNewSkillsToPosition: (positionId: string, skills: string[]) => Promise<IProjectPosition>;
  handleRemoveSkillsFromPosition: (positionId: string, skills: string[]) => Promise<IProjectPosition>;
}

export const POSITION_QUERY_PARAM = 'position';
export const APPLICANTS_QUERY_PARAM = 'applicants';
export const ADD_APPLICANT_QUERY_PARAM = 'addApplicant';

const ProjectOpportunityEntityPositions = ({
                            project,
                            positions,
                            handleRemovePosition,
                            refreshPositions,
                            handleGetApplicants,
                            handleAddApplicant,
                            ...handlers
                          }: IProjectPositionsProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleCloseModal = useCallback((key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleClickAddBtn = useCallback(() => {
    searchParams.set(POSITION_QUERY_PARAM, 'new');
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const positionQueryParam = useMemo(() => searchParams.get(POSITION_QUERY_PARAM), [searchParams]);
  const positionEntity = useMemo(() => (positions || []).find(pos => pos.id === positionQueryParam), [positions, positionQueryParam]);

  const applicantsQueryParam = useMemo(() => searchParams.get(APPLICANTS_QUERY_PARAM), [searchParams]);
  const applicantsForPosition = useMemo(() => (positions || []).find(pos => pos.id === applicantsQueryParam), [positions, applicantsQueryParam]);

  const addApplicantQueryParam = useMemo(() => searchParams.get(ADD_APPLICANT_QUERY_PARAM), [searchParams]);
  const addApplicantForPosition = useMemo(() => (positions || []).find(pos => pos.id === addApplicantQueryParam), [positions, addApplicantQueryParam]);

  return (
    <ColumnBoxFlexFullWidth>
      <FlexBoxFullWidth justifyContent={'space-between'} mb={2}>
        <Typography variant={'h3'}>{TEXTS.POSITIONS}</Typography>
        <Box>
          <Tooltip title={TEXTS.ADD_POSITION}>
            <IconButton onClick={handleClickAddBtn}>
              <Add color={'primary'}/>
            </IconButton>
          </Tooltip>
        </Box>
      </FlexBoxFullWidth>
      <PositionsTable
        project={project}
        positions={positions}
        handleRemovePosition={handleRemovePosition}
        refreshPositions={refreshPositions}
      />
      {
        (positionEntity || positionQueryParam === 'new') &&
        <PositionEntityModal
          positionEntity={positionEntity}
          refreshPositions={refreshPositions}
          onClose={() => handleCloseModal(POSITION_QUERY_PARAM)}
          {...handlers}
        />
      }
      {
        applicantsForPosition &&
        <PositionApplicantsModal
          onClose={() => handleCloseModal(APPLICANTS_QUERY_PARAM)}
          positionEntity={applicantsForPosition}
          handleGetApplicants={handleGetApplicants}
        />
      }
      {
        addApplicantForPosition && handleAddApplicant &&
        <AddApplicantModal
          positionEntity={addApplicantForPosition}
          handleAddApplicant={handleAddApplicant}
        />
      }
    </ColumnBoxFlexFullWidth>
  );
};

export default ProjectOpportunityEntityPositions;